import { useState, useEffect, useCallback } from 'react';
import NewsModal from '../News/NewsModal';
import { getMarketMovers, getWatchlistMovers, getWhyMoving, getUpcomingEvents, getMarketIndices } from '../../utils/api';
import SearchBar from '../Search/SearchBar';
import { fmt, colorByValue } from '../../utils/format';
import { t, getLang } from '../../utils/i18n';
import useLang from '../../hooks/useLang';

// ── Market Indices ─────────────────────────────────────────────
function Sparkline({ candles, positive }) {
  if (!candles || candles.length < 2) return null;
  const min = Math.min(...candles);
  const max = Math.max(...candles);
  const range = max - min || 1;
  const W = 80, H = 32;
  const pts = candles.map((v, i) => {
    const x = (i / (candles.length - 1)) * W;
    const y = H - ((v - min) / range) * H;
    return `${x},${y}`;
  }).join(' ');
  const color = positive ? 'var(--green)' : 'var(--red)';
  const fillPts = `0,${H} ${pts} ${W},${H}`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
      <polygon points={fillPts} fill={positive ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function IndexCard({ index }) {
  if (!index) return <div className="skeleton" style={{ height: 72, borderRadius: 10, flex: 1 }} />;
  const pos = index.changePercent >= 0;
  const chgColor = pos ? 'var(--green)' : 'var(--red)';
  const fmtPrice = (n) => n >= 10000
    ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{
      flex: 1, background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '12px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 4 }}>{index.label}</div>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{fmtPrice(index.price)}</div>
        <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: chgColor }}>
          {pos ? '+' : ''}{index.change?.toFixed(2)} ({pos ? '+' : ''}{index.changePercent?.toFixed(2)}%)
        </div>
      </div>
      <Sparkline candles={index.candles} positive={pos} />
    </div>
  );
}

function IndicesBar({ indices, loading }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
      {loading || !indices
        ? [0,1,2].map(i => <div key={i} className="skeleton" style={{ height: 72, borderRadius: 10, flex: 1 }} />)
        : indices.map(idx => <IndexCard key={idx.symbol} index={idx} />)
      }
    </div>
  );
}

// ── Collapsible section wrapper ─────────────────────────────────
function CollapsibleSection({ storageKey, label, sublabel, children }) {
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === null ? true : saved === 'true';
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(storageKey, String(next));
  };
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? 16 : 0 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>{sublabel}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{label}</div>
        </div>
        <button
          onClick={toggle}
          style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          {open ? '▲ Hide' : '▼ Show'}
        </button>
      </div>
      {open && children}
    </div>
  );
}

// Logo with fallback to colored initials
function StockLogo({ symbol, name, size = 40 }) {
  const [imgError, setImgError] = useState(false);
  const colors = ['#3b82f6','#8b5cf6','#f97316','#22c55e','#ec4899','#eab308','#ef4444','#06b6d4'];
  const color = colors[symbol.charCodeAt(0) % colors.length];
  const initials = symbol.slice(0, 2);

  if (imgError) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: color + '22', border: `2px solid ${color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.35, fontWeight: 700, color,
        flexShrink: 0,
      }}>{initials}</div>
    );
  }

  return (
    <img
      src={`https://assets.parqet.com/logos/symbol/${symbol}?format=png`}
      alt={symbol}
      onError={() => setImgError(true)}
      style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'contain', background: '#fff',
        border: '1px solid var(--border)', flexShrink: 0,
      }}
    />
  );
}

// Vertical bar chart of movers
function MoversChart({ movers, selected, onSelect, lang }) {
  if (!movers || movers.length === 0) return null;

  const maxAbs = Math.max(...movers.map(m => Math.abs(m.changePercent || 0)), 1);
  const MAX_BAR_H = 160;

  return (
    <div style={{
      background: '#0d0f12', borderRadius: 12, padding: '24px 20px 16px',
      display: 'flex', gap: 0, alignItems: 'flex-end', justifyContent: 'space-around',
      minHeight: 240, border: '1px solid var(--border)',
    }}>
      {movers.map(m => {
        const pct = m.changePercent || 0;
        const isPos = pct >= 0;
        const barH = Math.max((Math.abs(pct) / maxAbs) * MAX_BAR_H, 8);
        const isSelected = selected?.symbol === m.symbol;
        const color = isPos ? '#22c55e' : '#ef4444';
        const dimColor = isPos ? '#22c55e33' : '#ef444433';

        return (
          <div
            key={m.symbol}
            onClick={() => onSelect(m)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 8, cursor: 'pointer', flex: 1, maxWidth: 100,
              transition: 'transform 0.15s',
              transform: isSelected ? 'scale(1.05)' : 'scale(1)',
            }}
          >
            {/* % label */}
            <div style={{
              fontSize: 13, fontWeight: 700, color,
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
            </div>

            {/* Bar */}
            <div style={{
              width: 48, height: barH,
              borderRadius: 6,
              background: `linear-gradient(180deg, ${color} 0%, ${dimColor} 100%)`,
              border: isSelected ? `2px solid ${color}` : `1px solid ${color}44`,
              boxShadow: isSelected ? `0 0 12px ${color}66` : 'none',
              transition: 'all 0.2s',
              position: 'relative',
            }}>
              {isSelected && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: 4,
                  background: `${color}22`,
                }} />
              )}
            </div>

            {/* Logo */}
            <StockLogo symbol={m.symbol} name={m.name} size={36} />

            {/* Symbol + name + price + change */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{m.symbol}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>{m.name}</div>
              {m.price != null && (
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                  ${m.price.toFixed(2)}
                </div>
              )}
              {m.change != null && (
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color }}>
                  {m.change >= 0 ? '+' : ''}{m.change.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Why is it moving section
function WhyMoving({ mover, lang, onOpenStock }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [prevSymbol, setPrevSymbol] = useState(null);

  useEffect(() => {
    if (!mover) return;
    if (mover.symbol === prevSymbol) return;
    setPrevSymbol(mover.symbol);
    setState({ loading: true, data: null, error: null });

    getWhyMoving(mover.symbol, mover.name, mover.changePercent, getLang())
      .then(data => setState({ loading: false, data, error: null }))
      .catch(() => setState({ loading: false, data: null, error: 'Failed to load analysis.' }));
  }, [mover]);

  if (!mover) return null;

  const isPos = mover.changePercent >= 0;
  const color = isPos ? 'var(--green)' : 'var(--red)';

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '20px 24px', marginTop: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StockLogo symbol={mover.symbol} name={mover.name} size={32} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{mover.symbol}</span>
              <span style={{ fontFamily: 'JetBrains Mono', fontWeight: 700, fontSize: 15, color }}>{mover.changePercent >= 0 ? '+' : ''}{mover.changePercent.toFixed(2)}%</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{mover.name}</div>
          </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('whyMoving', lang)}</div>
      </div>

      {state.loading && (
        <div>
          <div className="skeleton" style={{ height: 14, width: '90%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 14, width: '75%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 14, width: '80%' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>{t('analysingMovement', lang)}</div>
        </div>
      )}

      {state.error && <div className="error-msg" style={{ fontSize: 13 }}>{state.error}</div>}

      {state.data && !state.loading && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, margin: '0 0 16px' }}>
            {state.data.explanation}
          </p>

          {state.data.news?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                {t('relatedNews', lang)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {state.data.news.map((n, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>·</span>
                    <span>{n.title} <span style={{ color: 'var(--text-muted)' }}>— {n.publisher}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            className="btn btn-ghost"
            onClick={() => onOpenStock(mover.symbol)}
            style={{ fontSize: 12, marginTop: 16 }}
          >
            {t('openAnalysis', lang)}
          </button>
        </div>
      )}
    </div>
  );
}

const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatEventDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return { day: d.getDate(), month: MONTHS[d.getMonth()], weekday: WEEKDAYS[d.getDay()] };
}

function UpcomingEvents({ events, loading, onSelect }) {
  if (loading) return (
    <div style={{ marginTop: 24 }}>
      {[1,2,3].map(i => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div className="skeleton" style={{ height: 14, width: 120, marginBottom: 10, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 56, borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );

  if (!events || events.length === 0) return (
    <div style={{ marginTop: 24, padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      No upcoming events in the next 90 days for your watchlist.
    </div>
  );

  // Group by date
  const groups = [];
  let cur = null;
  events.forEach(e => {
    if (!cur || cur.date !== e.date) { cur = { date: e.date, items: [] }; groups.push(cur); }
    cur.items.push(e);
  });

  return (
    <div style={{ marginTop: 24 }}>
      {groups.map(g => {
        const { day, month, weekday } = formatEventDate(g.date);
        return (
          <div key={g.date} style={{ marginBottom: 4 }}>
            {/* Date header */}
            <div style={{
              fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
              padding: '16px 0 8px', borderBottom: '1px solid var(--border-subtle)',
              marginBottom: 2,
            }}>
              <span style={{ fontSize: 16 }}>{day} {month}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8, fontWeight: 400 }}>{weekday}</span>
            </div>

            {g.items.map((ev, i) => {
              const isDividend = ev.type === 'dividend';
              const isEarnings = ev.type === 'earnings';
              const badgeColor = isDividend ? '#3b82f6' : '#f97316';
              const badgeBg = isDividend ? '#3b82f622' : '#f9731622';

              return (
                <div
                  key={i}
                  onClick={() => onSelect(ev.symbol)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 8px', borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer', transition: 'background 0.1s', borderRadius: 6,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <StockLogo symbol={ev.symbol} name={ev.name} size={36} />
                    <div>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{ev.symbol}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ev.name}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                      padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase',
                      background: badgeBg, color: badgeColor, border: `1px solid ${badgeColor}44`,
                    }}>
                      {isDividend ? 'Dividends' : 'Earnings'}
                    </span>
                    {ev.amount && (
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {ev.amount}
                      </span>
                    )}
                    {isEarnings && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Est. report date</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function AlertsList({ alerts, onSelect, onCancel }) {
  const fmtDate = (ts) => {
    const d = new Date(parseInt(ts));
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  if (!alerts || alerts.length === 0) {
    return <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>No active price alerts set.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {alerts.map(a => {
          const isAbove = a.condition === 'above';
          const color = isAbove ? 'var(--green)' : 'var(--red)';
          const dimBg = isAbove ? 'var(--green-dim)' : 'var(--red-dim)';
          return (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px', borderRadius: 10,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              cursor: 'pointer', transition: 'border-color 0.15s',
            }}
              onClick={() => onSelect(a.symbol)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              {/* Symbol + name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{a.symbol}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Set {fmtDate(a.created_at)}</div>
              </div>
              {/* Condition pill */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: dimBg, border: `1px solid ${color}`,
                borderRadius: 6, padding: '4px 12px',
                fontSize: 13, fontWeight: 700, color, flexShrink: 0,
              }}>
                <span>{isAbove ? '↑' : '↓'}</span>
                <span>${a.target_price.toFixed(2)}</span>
              </div>
              {/* Cancel button */}
              <button
                onClick={(e) => { e.stopPropagation(); onCancel(a.id); }}
                title="Cancel alert"
                style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '4px 8px', color: 'var(--text-muted)', cursor: 'pointer',
                  fontSize: 12, lineHeight: 1, flexShrink: 0,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
              >✕</button>
            </div>
          );
        })}
      </div>
  );
}

export default function LandingPage({ onSelect, activeAlerts = [], onAlertCancelled, watchlistSymbols = [] }) {
  const { lang } = useLang();
  const [tab, setTab] = useState('market'); // 'market' | 'watchlist'
  const [marketData, setMarketData] = useState(null);
  const [watchlistData, setWatchlistData] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [moversTab, setMoversTab] = useState('gainers'); // 'gainers' | 'losers'
  const [selectedMover, setSelectedMover] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [indices, setIndices] = useState(null);
  const [indicesLoading, setIndicesLoading] = useState(true);

  useEffect(() => {
    getMarketIndices()
      .then(d => { setIndices(d); setIndicesLoading(false); })
      .catch(() => setIndicesLoading(false));
  }, []);

  useEffect(() => {
    setMarketLoading(true);
    getMarketMovers()
      .then(d => { setMarketData(d); setMarketLoading(false); })
      .catch(() => setMarketLoading(false));
  }, []);

  useEffect(() => {
    if (!watchlistSymbols.length) { setEvents([]); return; }
    setEventsLoading(true);
    getUpcomingEvents(watchlistSymbols)
      .then(d => { setEvents(d); setEventsLoading(false); })
      .catch(() => { setEvents([]); setEventsLoading(false); });
  }, [watchlistSymbols]);

  useEffect(() => {
    if (tab !== 'watchlist') return;
    if (!watchlistSymbols.length) { setWatchlistData({ gainers: [], losers: [] }); return; }
    setWatchlistLoading(true);
    getWatchlistMovers(watchlistSymbols)
      .then(d => { setWatchlistData(d); setWatchlistLoading(false); })
      .catch(() => setWatchlistLoading(false));
  }, [tab, watchlistSymbols]);

  const currentData = tab === 'market' ? marketData : watchlistData;
  const loading = tab === 'market' ? marketLoading : watchlistLoading;
  const movers = currentData?.[moversTab] || [];

  const tabBtnStyle = (active) => ({
    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
    background: active ? 'var(--accent)' : 'var(--bg-surface)',
    color: active ? '#fff' : 'var(--text-muted)',
  });

  const subTabStyle = (active, color) => ({
    padding: '6px 16px', borderRadius: 6, border: `1px solid ${active ? color : 'var(--border)'}`,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
    background: active ? color + '22' : 'transparent',
    color: active ? color : 'var(--text-muted)',
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
          <img src="/freejini-icon.png" alt="Freejini" style={{ height: 56, width: 56, objectFit: 'contain' }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '0.12em', color: 'var(--text-primary)', lineHeight: 1.1 }}>FREE<span style={{ color: '#22c55e' }}>JINI</span></div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.18em', marginTop: 2 }}>DATA. INSIGHTS. GROWTH.</div>
          </div>
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Professional US equity research — search any stock to begin</div>
      </div>

      {/* Search bar */}
      <div style={{ maxWidth: 480, margin: '0 auto 24px' }}>
        <SearchBar onSelect={onSelect} placeholder="Search ticker or company name..." />
      </div>

      {/* Market Indices */}
      <IndicesBar indices={indices} loading={indicesLoading} />

      {/* Main Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, justifyContent: 'center' }}>
        <button style={tabBtnStyle(tab === 'market')} onClick={() => { setTab('market'); setSelectedMover(null); }}>
          📈 {t('market', lang)}
        </button>
        <button style={tabBtnStyle(tab === 'watchlist')} onClick={() => { setTab('watchlist'); setSelectedMover(null); }}>
          ★ {t('myWatchlist', lang)}
        </button>
      </div>

      {/* Card */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
        {/* Section header + gainers/losers toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>
              {tab === 'market' ? t('market', lang) : t('myWatchlist', lang)}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{t('dailyMovers', lang)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={subTabStyle(moversTab === 'gainers', 'var(--green)')} onClick={() => { setMoversTab('gainers'); setSelectedMover(null); }}>
              ↑ {t('topGainers', lang)}
            </button>
            <button style={subTabStyle(moversTab === 'losers', 'var(--red)')} onClick={() => { setMoversTab('losers'); setSelectedMover(null); }}>
              ↓ {t('topLosers', lang)}
            </button>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', minHeight: 240, padding: '0 8px' }}>
            {[120, 80, 100, 60, 90].map((h, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="skeleton" style={{ width: '60%', height: 12, borderRadius: 4 }} />
                <div className="skeleton" style={{ width: 48, height: h, borderRadius: 6 }} />
                <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                <div className="skeleton" style={{ width: '50%', height: 10, borderRadius: 4 }} />
              </div>
            ))}
          </div>
        )}

        {/* Empty watchlist */}
        {!loading && tab === 'watchlist' && (!watchlistData || currentData?.gainers?.length === 0 && currentData?.losers?.length === 0) && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            {t('noWatchlistStocks', lang)}
          </div>
        )}

        {/* Movers chart */}
        {!loading && movers.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textAlign: 'center' }}>
              {t('clickBarToSee', lang)}
            </div>
            <MoversChart movers={movers} selected={selectedMover} onSelect={setSelectedMover} lang={lang} />
          </>
        )}
      </div>

      {/* Why is it moving */}
      {selectedMover && (
        <WhyMoving mover={selectedMover} lang={lang} onOpenStock={onSelect} />
      )}

      {/* Upcoming Events */}
      <CollapsibleSection storageKey="freejini_events_open" sublabel="MARK YOUR CALENDAR" label="Upcoming Events">
        <UpcomingEvents events={events} loading={eventsLoading} onSelect={onSelect} />
      </CollapsibleSection>

      {/* Active Alerts Summary */}
      <CollapsibleSection storageKey="freejini_alerts_open" sublabel="TRADING SETUP" label="Active Price Alerts">
        <AlertsList
          alerts={activeAlerts}
          onSelect={onSelect}
          onCancel={async (id) => {
            try {
              const { getToken } = await import('../../utils/auth');
              const axios = (await import('axios')).default;
              const token = getToken();
              await axios.create({ baseURL: '/api' }).delete(`/alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
              onAlertCancelled?.();
            } catch {}
          }}
        />
      </CollapsibleSection>

      {/* Market Headlines */}
      <CollapsibleSection storageKey="freejini_news_open" sublabel="YAHOO FINANCE" label="Market Headlines">
        <MarketNews />
      </CollapsibleSection>
    </div>
  );
}

function MarketNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    import('axios').then(({ default: axios }) => {
      import('../../utils/auth').then(({ getToken }) => {
        axios.create({ baseURL: '/api', timeout: 15000 })
          .get('/market/news', { headers: { Authorization: `Bearer ${getToken()}` } })
          .then(r => setNews(r.data || []))
          .catch(() => {})
          .finally(() => setLoading(false));
      });
    });
  }, []);

  if (loading) return (
    <div>
      {[1,2,3].map(i => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ width: 70, height: 48, borderRadius: 6, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 14, borderRadius: 4, marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 10, borderRadius: 4, width: '40%' }} />
          </div>
        </div>
      ))}
    </div>
  );

  const timeAgo = (d) => { const m = Math.floor((Date.now() - new Date(d)) / 60000); return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`; };

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, textAlign: 'right' }}>
        Via Yahoo Finance · updated every 5 min · click to read
      </div>
      {news.map((item, i) => (
        <div
          key={item.id || i}
          onClick={() => setSelected(item)}
          style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {item.thumbnail && (
            <img src={item.thumbnail} alt="" style={{ width: 68, height: 46, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: i === 0 ? 700 : 500, color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 4,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.title}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{item.publisher}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>·</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(item.publishedAt)}</span>
            </div>
          </div>
        </div>
      ))}
      {selected && <NewsModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
